import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useBasket } from "@/context/BasketContext";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const provider = params.get("provider");
  const txnId = params.get("txn");
  const { clear } = useBasket();
  const [status, setStatus] = useState("polling");
  const [data, setData] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    const isFree = params.get("free") === "1";
    if (isFree) {
      setStatus("paid");
      setData({ free: true });
      clear();
      return;
    }
    const isSumup = provider === "sumup" && txnId;
    const isNochex = provider === "nochex" && txnId;
    if (!sessionId && !isSumup && !isNochex) {
      setStatus("error");
      return;
    }
    let cancelled = false;
    const poll = async () => {
      try {
        const url = isNochex
          ? `/payments/nochex/status/${txnId}`
          : isSumup
          ? `/payments/sumup/status/${txnId}`
          : `/payments/status/${sessionId}`;
        const { data } = await api.get(url);
        if (cancelled) return;
        setData(data);
        if (data.payment_status === "paid") {
          setStatus("paid");
          clear();
          return;
        }
        if (data.status === "expired" || data.payment_status === "failed") {
          setStatus("expired");
          return;
        }
        attempts.current += 1;
        if (attempts.current >= 15) {
          setStatus("timeout");
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        setStatus("error");
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, provider, txnId]);

  return (
    <div className="max-w-2xl mx-auto px-5 py-20" data-testid="payment-success-page">
      <div className="ufr-card rounded-md p-10 text-center">
        {status === "polling" && (
          <>
            <Loader2 className="mx-auto animate-spin text-[color:var(--ufr-green)]" size={40} />
            <h1 className="font-display text-3xl text-white mt-4">Confirming payment…</h1>
            <p className="text-white/60 mt-2 text-sm">Hold tight, we're securing your tickets.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="mx-auto text-[color:var(--ufr-green)]" size={48} />
            <h1 className="font-display text-4xl text-white mt-4">You're in!</h1>
            <p className="text-white/70 mt-2">
              {data?.free
                ? "Your free entries are secured. Good luck in the draw!"
                : data?.items && data.items.length > 1
                ? `${data.tickets} tickets across ${data.items.length} raffles secured. Good luck in the draws!`
                : `${data?.tickets} ticket${data?.tickets > 1 ? "s" : ""} secured. Good luck in the draw.`}
            </p>
            <p className="text-white/50 text-sm mt-2">A confirmation email is on its way.</p>
            <div className="flex gap-3 justify-center mt-6">
              <Link to="/account" className="btn-ufr-primary px-6 py-3 text-sm" data-testid="success-view-entries">View my entries</Link>
              <Link to="/competitions" className="btn-ufr-ghost px-6 py-3 text-sm">More raffles</Link>
            </div>
          </>
        )}
        {(status === "expired" || status === "error" || status === "timeout") && (
          <>
            <XCircle className="mx-auto text-red-400" size={40} />
            <h1 className="font-display text-3xl text-white mt-4">Payment not completed</h1>
            <p className="text-white/60 mt-2 text-sm">
              {status === "expired" ? "The session expired." : status === "timeout" ? "Still processing — check your account in a minute." : "Something went wrong checking your payment."}
            </p>
            <Link to="/competitions" className="btn-ufr-primary inline-block px-6 py-3 text-sm mt-6">Back to raffles</Link>
          </>
        )}
      </div>
    </div>
  );
}
